import { Suspense, useEffect, useState } from "react";
import { GoogleUsersCounter } from "../components/GoogleUsersCounter.jsx";
import { SiteFooter } from "../components/SiteFooter.jsx";
import { GoogleAccountButton } from "../contexts/SoloAuthContext.jsx";
import { useRoom } from "../contexts/RoomContext.jsx";
import { GAME_CATALOG } from "../data/gameCatalog.js";
import { RankingPage } from "./RankingPage.jsx";
import { PuzzleSoloPage } from "./PuzzleSoloPage.jsx";
import { StopSoloPage } from "./StopSoloPage.jsx";
import { TermSoloPage } from "./TermSoloPage.jsx";

const SCREEN_KEY = "@PartyGames:soloTela";
const SCREENS = ["termo-solo", "quebra-solo", "stop-solo", "ranking"];
const SOLO_SCREEN_BY_GAME = {
  termo: "termo-solo",
  "quebra-cabeca": "quebra-solo",
  stop: "stop-solo",
};

const initialScreen = () => {
  try {
    const saved = localStorage.getItem(SCREEN_KEY);
    return SCREENS.includes(saved) ? saved : "hub";
  } catch {
    return "hub";
  }
};

function GameLoading() {
  return (
    <div className="game-loading" role="status">
      <span aria-hidden="true" />
      <strong>Carregando jogo…</strong>
    </div>
  );
}

export function HomePage() {
  const { createRoom, joinRoom, loading, error } = useRoom();
  const [username, setUsername] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [localError, setLocalError] = useState("");
  const [screen, setScreen] = useState(initialScreen);

  useEffect(() => {
    try {
      screen === "hub"
        ? localStorage.removeItem(SCREEN_KEY)
        : localStorage.setItem(SCREEN_KEY, screen);
    } catch {}
  }, [screen]);

  const validUsername = () => {
    const value = username.trim();
    if (value.length >= 4) return value;
    setLocalError("Seu apelido deve ter pelo menos 4 letras!");
    return null;
  };
  const handleCreate = () => {
    setLocalError("");
    const value = validUsername();
    if (value) createRoom(value);
  };
  const handleJoin = (event) => {
    event.preventDefault();
    setLocalError("");
    const value = validUsername();
    if (!value) return;
    if (roomCode.length !== 4) {
      setLocalError("Digite as 4 letras do código da sala!");
      return;
    }
    joinRoom(roomCode, value);
  };

  const solo =
    screen === "termo-solo" ? (
      <TermSoloPage aoVoltar={() => setScreen("hub")} />
    ) : screen === "quebra-solo" ? (
      <PuzzleSoloPage aoVoltar={() => setScreen("hub")} />
    ) : screen === "stop-solo" ? (
      <StopSoloPage aoVoltar={() => setScreen("hub")} />
    ) : screen === "ranking" ? (
      <RankingPage aoVoltar={() => setScreen("hub")} />
    ) : null;
  if (solo) return <Suspense fallback={<GameLoading />}>{solo}</Suspense>;

  return (
    <div className="home-page portal-page">
      <main className="portal-shell">
        <header className="portal-header">
          <nav className="portal-nav" aria-label="Navegação principal">
            <a className="brand-lockup portal-brand" href="#jogos">
              <span className="brand-symbol" aria-hidden="true">
                <img src="/icone.png" alt="" />
              </span>
              <span>
                <strong>Party Games</strong>
                <small>Minigames com a galera</small>
              </span>
            </a>
            <div className="portal-links">
              <a
                className="portal-social portal-social-instagram"
                href="https://www.instagram.com/eulokismt"
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram
              </a>
              <a
                className="portal-social portal-social-twitch"
                href="https://www.twitch.tv/eulokismt"
                target="_blank"
                rel="noopener noreferrer"
              >
                Twitch
              </a>
            </div>
          </nav>
          <div className="portal-topline">
            <div className="portal-intro">
              <h1>Escolha um jogo e comece a partida.</h1>
              <p>Crie uma sala para jogar em grupo ou abra um modo solo.</p>
            </div>
            <div className="portal-account">
              <GoogleUsersCounter />
              <GoogleAccountButton local="inicio" />
            </div>
          </div>
          <div className="quick-play-panel">
            <div className="quick-player">
              <label htmlFor="home-username">Seu apelido</label>
              <input
                id="home-username"
                className="input-field"
                placeholder="Mínimo 4 letras"
                value={username}
                maxLength={15}
                autoComplete="nickname"
                onChange={(event) => {
                  setUsername(event.target.value);
                  setLocalError("");
                }}
              />
            </div>
            <div className="quick-room-actions">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleCreate}
                disabled={loading}
              >
                {loading ? "Criando…" : "Criar sala"}
              </button>
              <span className="quick-divider">ou</span>
              <form className="quick-join-form" onSubmit={handleJoin}>
                <label className="sr-only" htmlFor="home-room-code">
                  Código da sala
                </label>
                <input
                  id="home-room-code"
                  className="input-field input-code"
                  placeholder="ABCD"
                  value={roomCode}
                  onChange={(event) => {
                    setRoomCode(
                      event.target.value.toUpperCase().replace(/[^A-Z]/g, ""),
                    );
                    setLocalError("");
                  }}
                  autoCapitalize="characters"
                  maxLength={4}
                />
                <button className="btn btn-primary" disabled={loading}>
                  {loading ? "Entrando…" : "Entrar"}
                </button>
              </form>
            </div>
          </div>
        </header>

        {(error || localError) && (
          <div className="alert-error portal-alert" role="alert">
            {error || localError}
          </div>
        )}

        <section id="jogos" className="catalog-section">
          <div className="catalog-header">
            <div>
              <h2>Jogos disponíveis</h2>
              <p>Escolha o que a turma vai jogar.</p>
            </div>
            <button
              className="ranking-link catalog-ranking"
              onClick={() => setScreen("ranking")}
              type="button"
            >
              🏆 Ranking
            </button>
          </div>
          <div className="catalog-grid">
            {GAME_CATALOG.map((game) => {
              const soloScreen = SOLO_SCREEN_BY_GAME[game.id];
              return (
                <article
                  className={`catalog-card ${game.emBreve ? "disabled" : ""}`}
                  key={game.id}
                >
                  <div className="catalog-cover">
                    <img src={game.imagem} alt={`Capa do jogo ${game.nome}`} />
                    {game.emBreve && <span>Em breve</span>}
                  </div>
                  <div className="catalog-info">
                    <h3>{game.nome}</h3>
                    <p>{game.descricao}</p>
                    {!game.emBreve &&
                      (soloScreen ? (
                        <button
                          onClick={() => setScreen(soloScreen)}
                          type="button"
                        >
                          Jogar solo
                        </button>
                      ) : (
                        <span className="group-only">Disponível nas salas</span>
                      ))}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
        <SiteFooter />
      </main>
    </div>
  );
}
